import React, { useEffect } from "react";
import type { ReactElement } from "react";
import { useState, useRef } from "react";
import {
  Box,
  Grid,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  EditOutlined,
  Language,
  LocalShipping,
  SendOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import PrintDialog from "./components/PrintDocuments";
import UploadDocuments from "./components/UploadDocuments";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import type { VenueData } from "../venues/types/venues.types";
import { useBookingData } from "./view-booking/hooks/UseBookingData";
import { useDocuments } from "./view-booking/hooks/UseDocuments";
import type { DocumentItem } from "../../types/documents";
import BelovedCard from "./view-booking/BelovedCard";
import DetailsCard from "./view-booking/DetailsCard";
import PackageCard from "./view-booking/PackageCard";
import StreamsSection from "./view-booking/StreamsSection";
import DocumentsList from "./view-booking/DocumentsList";
import AuditFooter from "./view-booking/AuditFooter";
import OrganizerCard from "./view-booking/OrganizerCard";
import VenueDetailsDialog from "./view-booking/VenueDialog";
import MemoryTableView2 from "../stream-management/components/memory-table/MemoryTableView2";
import ViewBookingPage2 from "./view-booking2/ViewBooking2";
import { BaseURL } from "../../service/Api";
import BookingReviewSummaryCard from "./view-booking2/components/BookingReviewSummaryCard";

const ViewBookingPage: React.FC = (): ReactElement => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { bookingData, documents, loading, error } = useBookingData();
  const { handleViewDocument, handleSaveDocument, handleDocumentDelete } =
    useDocuments();

  const [streamTab, setStreamTab] = useState(0);
  const [bookingTab, setBookingTab] = useState(0);
  const [documentsList, setDocumentsList] = useState<DocumentItem[]>(documents);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [dialogDocumentOpen, setDialogDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentCategory, setDocumentCategory] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDescription, setDocumentDescription] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [documentToDeleteId, setDocumentToDeleteId] = useState<string | null>(
    null,
  );

  const [memoryStreamTab, setMemoryStreamTab] = useState(0);

  useEffect(() => {
    if (bookingTab === 1) setMemoryStreamTab(streamTab);
  }, [bookingTab, streamTab]);

  const [hoverVenue, setHoverVenue] = useState<Partial<VenueData> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedDocumentType, setSelectedDocumentType] = useState<
    "internet-form" | "memory-delivery-note"
  >("internet-form");

  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentStreamId = bookingData?.streams[streamTab]?.id;

  React.useEffect(() => {
    setDocumentsList(documents);
  }, [documents]);

  const handlePackageMouseEnter = () => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    // Start delayed open
    openTimeoutRef.current = setTimeout(() => {
      const currentStream = bookingData?.streams?.[streamTab].venueDetails;

      if (!currentStream) return;

      setHoverVenue({
        name: currentStream.name || "N/A",
        address_one: currentStream.address_one || "N/A",
        address_two: currentStream.address_two || "N/A",
        city: currentStream.city || "N/A",
        suburb: currentStream.suburb || "N/A",
        id: currentStream.id,
        files: currentStream.files,
        layouts: currentStream.layouts,
      });

      setIsModalOpen(true);
    }, 800);
  };

  const handlePackageMouseLeave = () => {
    // Cancel delayed open
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    // Delay close (smooth UX)
    closeTimeoutRef.current = setTimeout(() => {
      setIsModalOpen(false);
    }, 150);
  };

  const handleModalMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  const handlePackageClick = () => {
    window.open(`/venues/view/${bookingData?.streams[streamTab].id}`, "_blank");
  };

  const streamTitle = bookingData?.streams[streamTab]?.title || "";
  const prefix = "Stream for ";
  let nameForSlug = streamTitle;
  const startsWithPrefix = streamTitle.startsWith(prefix);

  if (startsWithPrefix) {
    nameForSlug = streamTitle.slice(prefix.length);
  }

  const fullName = nameForSlug.trim();
  const slug = encodeURIComponent(fullName.replace(/\s+/g, "-"));

  const handleActionButtonClick = (actionType: string) => {
    if (!currentStreamId) {
      console.warn(`No stream selected for ${actionType} action.`);
      return;
    }

    let path = "";

    if (actionType === "Callsheet") {
      path = `/stream-management/view/${currentStreamId}/callsheet/`;
      navigate(path);
      return;
    }

    if (bookingData?.service === "Conference") {
      const conferenceSlug = encodeURIComponent(
        bookingData.conferenceDetails?.conference_title?.replace(/\s+/g, "-") ||
          "conference",
      );
      path = `/conference-page/${id}/${conferenceSlug}`;
      navigate(path);
      return;
    }

    if (bookingData?.service === "Church") {
      const churchTitle = encodeURIComponent(
        bookingData.churchDetails?.church_name?.replace(/\s+/g, "-") ||
          "church",
      );
      path = `/church-page/${id}/${churchTitle}`;
      navigate(path);
      return;
    }

    switch (actionType) {
      case "Memorial Page":
        path = `/memorial-page/${id}/${slug}`;
        break;
      case "Share":
        console.warn("Share action not available.");
        return;
      default:
        console.warn("Unknown action type:", actionType);
        return;
    }

    if (path) navigate(path);
  };

  const handleAddDocument = () => {
    setDocumentName("");
    setDocumentDescription("");
    setDocumentCategory("");
    setDocumentFile(null);
    setDialogDocumentOpen(true);
  };

  const handleSaveDocumentClick = async () => {
    if (!id) return;

    setDocumentLoading(true);
    const newDoc = await handleSaveDocument(
      id,
      documentName,
      documentCategory,
      documentDescription,
      documentFile!,
    );

    if (newDoc) {
      setDocumentsList((prev) => [...prev, newDoc]);
      setDialogDocumentOpen(false);
      setDocumentName("");
      setDocumentFile(null);
    }

    setDocumentLoading(false);
  };

  const confirmDelete = (id: string) => {
    setDocumentToDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleDocumentDeleteClick = async () => {
    if (documentToDeleteId === null) {
      console.error("No document ID selected for deletion.");
      return;
    }

    const success = await handleDocumentDelete(documentToDeleteId);

    if (success) {
      setDocumentsList((prevDocuments) =>
        prevDocuments.filter((doc) => doc.id !== documentToDeleteId),
      );
      handleCloseDeleteDialog();
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDocumentToDeleteId(null);
  };
  console.log(bookingData?.reference_number);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !bookingData) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error" variant="h6">
          {error || "No booking data found."}
        </Typography>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="contained"
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (BaseURL === "https://staging.muzukuru.com/") {
    return <ViewBookingPage2 />;
  }

  return (
    <Box sx={{ height: "100%", mb: 2 }}>
      {/* Header Tabs and Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Tabs
          value={bookingTab}
          onChange={(e, newValue) => setBookingTab(newValue)}
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.primary[400],
              height: 2,
            },
          }}
          sx={{
            mb: 2,
            ".MuiTab-root": {
              color: colors.primary[300],
            },
            ".Mui-selected": {
              color: "#614338",
            },
          }}
        >
          <Tab label="Booking Details" />
          <Tab label="Memory Table" />
        </Tabs>
        <Box>
          <Button
            startIcon={<SendOutlined />}
            variant="outlined"
            onClick={() =>
              navigate("/proposals/proposals-builder", {
                state: { bookingId: id },
              })
            }
            sx={{
              mr: 1,
              borderColor: colors.primary[100],
              color: colors.primary[100],
            }}
          >
            Create Proposal
          </Button>
          <Button
            startIcon={<Language />}
            variant="outlined"
            sx={{
              mr: 1,
              borderColor: colors.primary[100],
              color: colors.primary[100],
            }}
            onClick={() => {
              setSelectedDocumentType("internet-form");
              setPrintDialogOpen(true);
            }}
          >
            Internet Agreement Form
          </Button>
          <Button
            startIcon={<LocalShipping />}
            variant="outlined"
            sx={{
              mr: 1,
              borderColor: colors.primary[100],
              color: colors.primary[100],
            }}
            onClick={() => {
              setSelectedDocumentType("memory-delivery-note");
              setPrintDialogOpen(true);
            }}
          >
            Memory Delivery Note
          </Button>

          <Button
            startIcon={<AddCircleOutlineOutlined />}
            variant="contained"
            onClick={() => navigate(`/stream-management/add`)}
            color="primary"
            sx={{ mr: 1 }}
          >
            Add Stream
          </Button>
          {bookingData.service === "church-funeral" && (
            <Button
              sx={{
                border: `2px solid ${colors.primary[100]}`,
                color: colors.primary[100],
                mr: 1,
              }}
              onClick={() =>
                navigate(`/bookings/${id}/church-form`, {
                  state: { bookingId: id },
                })
              }
            >
              Church Internal Form
            </Button>
          )}

          <Button
            startIcon={<EditOutlined />}
            onClick={() => navigate(`/bookings/${id}/edit`)}
            sx={{
              color: colors.primary[900],
              backgroundColor: colors.primary[400],
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            sx={{
              mr: 1,
              borderColor: colors.primary[100],
              color: colors.primary[100],
            }}
            onClick={() => {
              navigate(`/review/${bookingData.reference_number}`);
            }}
          >
            View Review
          </Button>
        </Box>
      </Box>
      {bookingTab === 0 && (
        <Grid container spacing={2}>
          {/* Organizer Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <OrganizerCard
              id={id || ""}
              organizer={bookingData.organiser}
              serviceType={bookingData.services?.[0]?.type_of_service}
            />
          </Grid>

          {/* Beloved Card */}
          {(bookingData.service === "Funeral" ||
            bookingData.service === "Funeral_Home") &&
            bookingData.beloved && (
              <Grid size={{ xs: 12, md: 4 }}>
                <BelovedCard
                  name={bookingData.beloved.name}
                  avatar={bookingData.beloved.avatar}
                  details={bookingData.beloved.details}
                />
              </Grid>
            )}

          {/* Church Details */}
          {bookingData.service === "Church" && bookingData.churchDetails && (
            <Grid size={{ xs: 12, md: 4 }}>
              <DetailsCard
                title="Church Details"
                avatar="/church-avatar.jpg"
                entityName={bookingData.churchDetails.church_name}
                details={
                  bookingData.churchDetails as unknown as Record<
                    string,
                    string | number
                  >
                }
              />
            </Grid>
          )}

          {/* Conference Details */}
          {bookingData.service === "Conference" && (
            <Grid size={{ xs: 12, md: 4 }}>
              <DetailsCard
                title="Conference Details"
                details={
                  bookingData.conferenceDetails as unknown as Record<
                    string,
                    string | number
                  >
                }
                hideAvatarName
              />
            </Grid>
          )}

          {/* Content Details */}
          {bookingData.service === "Other" && bookingData.contentDetails && (
            <Grid size={{ xs: 12, md: 4 }}>
              <DetailsCard
                title="Content Details"
                avatar="/default-avatar.jpg"
                entityName={bookingData.contentDetails.title}
                details={
                  bookingData.contentDetails as unknown as Record<
                    string,
                    string | number
                  >
                }
              />
            </Grid>
          )}

          {/* Package Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <PackageCard
              name={bookingData.package.name}
              price={bookingData.package.price}
              items={bookingData.package.items}
              total={bookingData.package.total}
            />
          </Grid>

          {/* Streams Section */}
          <Grid size={{ xs: 12, md: 8 }}>
            <StreamsSection
              streams={bookingData.streams}
              currentTab={streamTab}
              onTabChange={(e, newValue) => setStreamTab(newValue)}
              onViewStream={() => {
                const streamId = bookingData.streams[streamTab]?.id;
                if (streamId) {
                  navigate(`/stream-management/view/${streamId}`);
                }
              }}
              onActionClick={handleActionButtonClick}
              disableActions={!currentStreamId}
              bookingService={bookingData.service}
              onVenueHover={handlePackageMouseEnter}
              onVenueLeave={handlePackageMouseLeave}
              onVenueClick={handlePackageClick}
            />
          </Grid>

          {/* Documents List */}
          <Grid size={{ xs: 12, md: 4 }}>
            <DocumentsList
              documents={documentsList}
              onAddDocument={handleAddDocument}
              onViewDocument={handleViewDocument}
              onDeleteDocument={confirmDelete}
              hasDeletePermission={hasPermission(
                "booking.delete_bookingdocument",
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <BookingReviewSummaryCard bookingId={id || ""} />
          </Grid>
        </Grid>
      )}
      {bookingTab === 1 && (
        <Box>
          <Tabs
            value={memoryStreamTab}
            onChange={(e, newValue) => setMemoryStreamTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: { backgroundColor: colors.primary[400], height: 2 },
            }}
            sx={{
              mb: 2,
              ".MuiTab-root": {
                color: colors.primary[300],
                textTransform: "none",
              },
              ".Mui-selected": { color: "#614338" },
            }}
          >
            {bookingData.streams.map((s, idx) => (
              <Tab key={s.id} label={`Stream ${idx + 1}`} />
            ))}
          </Tabs>

          <MemoryTableView2
            streamId={String(bookingData.streams[memoryStreamTab]?.id)}
          />
        </Box>
      )}

      {/* Dialogs and Modals */}
      <PrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        documentType={selectedDocumentType}
      />

      <UploadDocuments
        open={dialogDocumentOpen}
        setDialogDocumentOpen={setDialogDocumentOpen}
        documentName={documentName}
        documentCategory={documentCategory}
        loading={documentLoading}
        documentDescription={documentDescription}
        setDocumentDescription={setDocumentDescription}
        setDocumentName={setDocumentName}
        setDocumentCategory={setDocumentCategory}
        setDocumentFile={setDocumentFile}
        documentFile={documentFile}
        handleSaveDocument={handleSaveDocumentClick}
      />

      {/* Audit Footer */}
      {(bookingData.created_by || bookingData.last_updated_by) && (
        <AuditFooter
          createdBy={
            typeof bookingData.created_by === "object"
              ? bookingData.created_by
              : null
          }
          lastUpdatedBy={bookingData.last_updated_by ?? null}
        />
      )}

      <VenueDetailsDialog
        open={isModalOpen}
        venue={
          hoverVenue
            ? {
                ...hoverVenue,
                layouts: hoverVenue.layouts?.map((l) => ({
                  capacity: Number(l.capacity),
                })),
              }
            : null
        }
        onClose={() => setIsModalOpen(false)}
        onHover={handleModalMouseEnter}
        onLeave={handlePackageMouseLeave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete this document? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDocumentDeleteClick}
            sx={{ color: "#EB4335" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewBookingPage;
